/**
 * Kanban Column Component
 * A sortable column for the Kanban board
 */

import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { cn } from '@/lib/utils';
import { TaskCard } from './TaskCard';
import type { KanbanColumn as KanbanColumnType, TaskListItem } from '../types';
import type { QuoteOption } from '../api';

// Status-specific styling
const STATUS_STYLES: Record<string, { headerBg: string; borderColor: string }> = {
  todo: {
    headerBg: 'bg-slate-100 dark:bg-slate-800',
    borderColor: 'border-t-slate-400',
  },
  in_progress: {
    headerBg: 'bg-blue-50 dark:bg-blue-950',
    borderColor: 'border-t-blue-500',
  },
  blocked: {
    headerBg: 'bg-red-50 dark:bg-red-950',
    borderColor: 'border-t-red-500',
  },
  done: {
    headerBg: 'bg-green-50 dark:bg-green-950',
    borderColor: 'border-t-green-500',
  },
  cancelled: {
    headerBg: 'bg-gray-100 dark:bg-gray-800',
    borderColor: 'border-t-gray-400',
  },
};

interface KanbanColumnProps {
  column: KanbanColumnType;
  onTaskClick: (task: TaskListItem) => void;
  activeTaskId?: string | null;
  quotes?: QuoteOption[];
}

export function KanbanColumn({ column, onTaskClick, activeTaskId, quotes = [] }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: column.status,
    data: {
      type: 'column',
      status: column.status,
    },
  });

  const styles = STATUS_STYLES[column.status] || STATUS_STYLES.todo;
  const taskIds = column.tasks.map((task) => task.id);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        // flex-1 so columns share the available width on wide screens; min-w
        // keeps cards readable and triggers horizontal scroll on narrow ones.
        'flex flex-col flex-1 min-w-[280px] rounded-lg border border-t-4 bg-muted/30',
        styles.borderColor,
        isOver && 'ring-2 ring-primary ring-offset-2'
      )}
    >
      {/* Column Header */}
      <div className={cn('p-3 rounded-t-lg', styles.headerBg)}>
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-sm">{column.label}</h3>
          <span className="text-xs text-muted-foreground bg-background rounded-full px-2 py-0.5">
            {column.tasks.length}
          </span>
        </div>
      </div>

      {/* Tasks Container */}
      <div
        className={cn(
          'flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px] max-h-[calc(100vh-300px)]',
          'atek-thin-scrollbar',
          isOver && 'bg-primary/5'
        )}
      >
        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          {column.tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={() => onTaskClick(task)}
              isDragging={activeTaskId === task.id}
              quotes={quotes}
            />
          ))}
        </SortableContext>

        {/* Empty state */}
        {column.tasks.length === 0 && (
          <div className="flex items-center justify-center h-24 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
            Drop tasks here
          </div>
        )}
      </div>
    </div>
  );
}
